/**
 * Bővített UTILMD XML Generátor
 * Támogatja a standard EDIFACT és a magyar ISU_UTILMD (SAP) formátumokat
 * 
 * Mérlegkörök (Balance Groups):
 * - MVM: MVM Next Energiakereskedelmi Zrt. (Kód: 15X-DENERGIA---J)
 * - EON: EON Energiamegoldások Kft. (Kód: 39XEON-ENMEGOLDC)
 * 
 * A mérlegkör kódok használata:
 * - UTILMD üzenetek generálásakor
 * - Kervát táblázatokban
 * - Ügyfél adatoknál (balanceGroup mező)
 */

import { 
  PODChangeRequest,
  ExtendedPODChangeRequest, 
  UTILMDFormat, 
  UTILMDMarketSettings, 
  UTILMDGeneratorConfig,
  ISUPartnerInfo
} from '../types/utilmd';
import { BalanceGroupType, BALANCE_GROUPS } from '../types/customer';

export class UTILMDGenerator {
  private config: UTILMDGeneratorConfig;

  constructor(config?: Partial<UTILMDGeneratorConfig>) {
    this.config = {
      format: 'ISU_UTILMD', // Magyar piac alapértelmezett
      marketSettings: {
        format: 'ISU_UTILMD',
        market: 'HU',
        version: '3.0',
        encoding: 'UTF-8'
      },
      defaultSender: {
        eicCode: '15X-ENERGIA-INFO-T',
        partnerType: 'MS',
        companyName: 'Energia Info Kereskedőház Kft.',
        address: 'Budapest, Magyarország'
      },
      validation: {
        strictPODFormat: true,
        validateEICCodes: true,
        checkDateRanges: true
      },
      output: {
        prettifyXML: true,
        addComments: false,
        includeTimestamp: true
      },
      ...config
    };
  }

  /**
   * Fő XML generálási metódus - automatikusan a konfigurált formátum szerint
   */
  public generateXML(request: PODChangeRequest | ExtendedPODChangeRequest): string {
    const extendedRequest = this.extendRequest(request);
    
    switch (this.config.format) {
      case 'ISU_UTILMD':
        return this.generateISUUTILMD(extendedRequest);
      case 'EDIFACT':
        return this.generateEDIFACTUTILMD(extendedRequest);
      default:
        throw new Error(`Ismeretlen UTILMD formátum: ${this.config.format}`);
    }
  }

  /**
   * ISU_UTILMD (SAP) formátum generálása - Magyar piac
   */
  public generateISUUTILMD(request: ExtendedPODChangeRequest): string {
    const now = new Date();
    const timestamp = this.formatTimestamp(now);
    const documentNumber = this.generateDocumentNumber(request, now);
    
    const messageType = this.mapMessageTypeToISU(request.messageType);
    const documentFunction = request.documentFunction || '9'; // 9 = Original document
    
    const xml = `<?xml version="1.0" encoding="${this.config.marketSettings.encoding}"?>
<ISU_UTILMD${this.config.output.addComments ? ' <!-- Hungarian Market ISU_UTILMD Format -->' : ''}>
  <IDOC BEGIN="1">
    <EDI_DC40 SEGMENT="1">
      <TABNAM>EDI_DC40</TABNAM>
      <MANDT>100</MANDT>
      <DOCNUM>${documentNumber}</DOCNUM>
      <DOCREL>${this.config.marketSettings.version}</DOCREL>
      <STATUS>30</STATUS>
      <DIRECT>1</DIRECT>
      <OUTMOD>2</OUTMOD>
      <EXPRSS>X</EXPRSS>
      <IDOCTYP>UTILMD_HU</IDOCTYP>
      <MESTYP>UTILMD</MESTYP>
      <MESCOD>HU</MESCOD>
      <SNDPOR>SAP${request.provider}</SNDPOR>
      <SNDPRT>LS</SNDPRT>
      <SNDPRN>${this.getProviderSAPCode(request.provider)}</SNDPRN>
      <RCVPOR>SAPENERGIA</RCVPOR>
      <RCVPRT>LS</RCVPRT>
      <RCVPRN>${this.config.defaultSender.eicCode}</RCVPRN>
      <CREDAT>${this.formatSAPDate(now)}</CREDAT>
      <CRETIM>${this.formatSAPTime(now)}</CRETIM>
    </EDI_DC40>
    
    <!-- UNH - Message Header -->
    <E1VDEWUNH SEGMENT="1">
      <MANDT>100</MANDT>
      <DOCNUM>${documentNumber}</DOCNUM>
      <SEGNUM>1</SEGNUM>
      <PSGNUM>0</PSGNUM>
      <HLEVEL>01</HLEVEL>
      <MSGTYP>UTILMD</MSGTYP>
      <MSGVER>D</MSGVER>
      <MSGREL>03B</MSGREL>
      <MSGAGC>UN</MSGAGC>
      <MSGASS>EAN008</MSGASS>
      <MSGREF>${documentNumber}</MSGREF>
    </E1VDEWUNH>
    
    <!-- BGM - Beginning of Message -->
    <E1VDEWBGM_1 SEGMENT="1">
      <MANDT>100</MANDT>
      <DOCNUM>${documentNumber}</DOCNUM>
      <SEGNUM>2</SEGNUM>
      <PSGNUM>1</PSGNUM>
      <HLEVEL>02</HLEVEL>
      <n>${messageType}</n>
      <QUALIFIER>ZZZ</QUALIFIER>
      <DOCUMENTNO>${documentNumber}</DOCUMENTNO>
      <DOCUMENTFUNC>${documentFunction}</DOCUMENTFUNC>
    </E1VDEWBGM_1>
    
    <!-- DTM - Date/Time -->
    <E1VDEWDTM_1 SEGMENT="1">
      <MANDT>100</MANDT>
      <DOCNUM>${documentNumber}</DOCNUM>
      <SEGNUM>3</SEGNUM>
      <PSGNUM>2</PSGNUM>
      <HLEVEL>02</HLEVEL>
      <QUALIFIER>137</QUALIFIER>
      <DATE>${this.formatSAPDate(now)}</DATE>
      <TIME>${this.formatSAPTime(now)}</TIME>
      <FORMAT>102</FORMAT>
    </E1VDEWDTM_1>
    
    <!-- RFF - Reference -->
    <E1VDEWRFF_1 SEGMENT="1">
      <MANDT>100</MANDT>
      <DOCNUM>${documentNumber}</DOCNUM>
      <SEGNUM>4</SEGNUM>
      <PSGNUM>2</PSGNUM>
      <HLEVEL>02</HLEVEL>
      <QUALIFIER>Z13</QUALIFIER>
      <REFNO>${request.podId}</REFNO>
    </E1VDEWRFF_1>
    
    <!-- NAD - Sender (Message Sender) -->
    <E1VDEWNAD_3 SEGMENT="1">
      <MANDT>100</MANDT>
      <DOCNUM>${documentNumber}</DOCNUM>
      <SEGNUM>5</SEGNUM>
      <PSGNUM>2</PSGNUM>
      <HLEVEL>02</HLEVEL>
      <PARTNERFUNCTION>MS</PARTNERFUNCTION>
      <PARTNERID>${this.config.defaultSender.eicCode}</PARTNERID>
      <PARTNERCODE>9</PARTNERCODE>
    </E1VDEWNAD_3>
    
    <!-- NAD - Receiver (Market Operator) -->
    <E1VDEWNAD_3 SEGMENT="1">
      <MANDT>100</MANDT>
      <DOCNUM>${documentNumber}</DOCNUM>
      <SEGNUM>6</SEGNUM>
      <PSGNUM>2</PSGNUM>
      <HLEVEL>02</HLEVEL>
      <PARTNERFUNCTION>MR</PARTNERFUNCTION>
      <PARTNERID>${this.getProviderEIC(request.provider)}</PARTNERID>
      <PARTNERCODE>9</PARTNERCODE>
    </E1VDEWNAD_3>
    
    <!-- UNS - Section Control -->
    <E1VDEWUNS SEGMENT="1">
      <MANDT>100</MANDT>
      <DOCNUM>${documentNumber}</DOCNUM>
      <SEGNUM>7</SEGNUM>
      <PSGNUM>2</PSGNUM>
      <HLEVEL>02</HLEVEL>
      <SECTIONID>D</SECTIONID>
    </E1VDEWUNS>
    
    <!-- NAD - Customer (Invoice Party) -->
    <E1VDEWNAD_3 SEGMENT="1">
      <MANDT>100</MANDT>
      <DOCNUM>${documentNumber}</DOCNUM>
      <SEGNUM>8</SEGNUM>
      <PSGNUM>7</PSGNUM>
      <HLEVEL>03</HLEVEL>
      <PARTNERFUNCTION>IV</PARTNERFUNCTION>
      <PARTNERNAME>${this.escapeXML(request.customerName)}</PARTNERNAME>
      <STREET>${this.escapeXML(request.customerAddress)}</STREET>
    </E1VDEWNAD_3>
    
    <!-- LOC - Location (POD) -->
    <E1VDEWLOC_1 SEGMENT="1">
      <MANDT>100</MANDT>
      <DOCNUM>${documentNumber}</DOCNUM>
      <SEGNUM>9</SEGNUM>
      <PSGNUM>8</PSGNUM>
      <HLEVEL>03</HLEVEL>
      <QUALIFIER>172</QUALIFIER>
      <LOCATIONID>${request.podId}</LOCATIONID>
      <LOCATIONCODE>9</LOCATIONCODE>
    </E1VDEWLOC_1>
    
    <!-- DTM - Contract Start Date -->
    <E1VDEWDTM_1 SEGMENT="1">
      <MANDT>100</MANDT>
      <DOCNUM>${documentNumber}</DOCNUM>
      <SEGNUM>10</SEGNUM>
      <PSGNUM>9</PSGNUM>
      <HLEVEL>03</HLEVEL>
      <QUALIFIER>194</QUALIFIER>
      <DATE>${this.formatContractDate(request.contractStartDate)}</DATE>
      <FORMAT>102</FORMAT>
    </E1VDEWDTM_1>
    
    <!-- DTM - Contract End Date -->
    <E1VDEWDTM_1 SEGMENT="1">
      <MANDT>100</MANDT>
      <DOCNUM>${documentNumber}</DOCNUM>
      <SEGNUM>11</SEGNUM>
      <PSGNUM>9</PSGNUM>
      <HLEVEL>03</HLEVEL>
      <QUALIFIER>206</QUALIFIER>
      <DATE>${this.formatContractDate(request.contractEndDate)}</DATE>
      <FORMAT>102</FORMAT>
    </E1VDEWDTM_1>
    
    <!-- RFF - Message Type Reference -->
    <E1VDEWRFF_1 SEGMENT="1">
      <MANDT>100</MANDT>
      <DOCNUM>${documentNumber}</DOCNUM>
      <SEGNUM>12</SEGNUM>
      <PSGNUM>9</PSGNUM>
      <HLEVEL>03</HLEVEL>
      <QUALIFIER>Z02</QUALIFIER>
      <REFNO>${this.mapMessageTypeToProcess(request.messageType)}</REFNO>
    </E1VDEWRFF_1>
    
    <!-- NAD - New Trader -->
    <E1VDEWNAD_3 SEGMENT="1">
      <MANDT>100</MANDT>
      <DOCNUM>${documentNumber}</DOCNUM>
      <SEGNUM>13</SEGNUM>
      <PSGNUM>9</PSGNUM>
      <HLEVEL>03</HLEVEL>
      <PARTNERFUNCTION>Z01</PARTNERFUNCTION>
      <PARTNERID>${request.newTrader}</PARTNERID>
      <PARTNERCODE>9</PARTNERCODE>
    </E1VDEWNAD_3>
    
    ${request.oldTrader ? `<!-- NAD - Old Trader -->
    <E1VDEWNAD_3 SEGMENT="1">
      <MANDT>100</MANDT>
      <DOCNUM>${documentNumber}</DOCNUM>
      <SEGNUM>14</SEGNUM>
      <PSGNUM>9</PSGNUM>
      <HLEVEL>03</HLEVEL>
      <PARTNERFUNCTION>Z02</PARTNERFUNCTION>
      <PARTNERID>${request.oldTrader}</PARTNERID>
      <PARTNERCODE>9</PARTNERCODE>
    </E1VDEWNAD_3>` : ''}
    
    <!-- UNT - Message Trailer -->
    <E1VDEWUNT SEGMENT="1">
      <MANDT>100</MANDT>
      <DOCNUM>${documentNumber}</DOCNUM>
      <SEGNUM>15</SEGNUM>
      <PSGNUM>1</PSGNUM>
      <HLEVEL>02</HLEVEL>
      <SEGMENTCOUNT>1</SEGMENTCOUNT>
      <MSGREF>${documentNumber}</MSGREF>
    </E1VDEWUNT>
    
  </IDOC>
</ISU_UTILMD>`;

    return this.config.output.prettifyXML ? this.prettifyXML(xml) : xml;
  }

  /**
   * Standard EDIFACT UTILMD formátum generálása - EU szabvány
   */
  public generateEDIFACTUTILMD(request: ExtendedPODChangeRequest): string {
    const now = new Date();
    const timestamp = now.toISOString().replace(/[-:]/g, '').substring(0, 15);
    const referenceNumber = `${request.provider}_${request.podId}_${timestamp}`;
    
    const xml = `<?xml version="1.0" encoding="${this.config.marketSettings.encoding}"?>
<UTILMD xmlns="urn:entsoe.eu:wgedi:utilmd:iec62325:351:3:utilmd-marketdocument:3:0"${this.config.output.addComments ? ' <!-- Standard EDIFACT UTILMD Format -->' : ''}>
  <UNH>
    <S001>
      <E0062>UTILMD</E0062>
      <E0065>D</E0065>
      <E0052>03B</E0052>
      <E0054>UN</E0054>
      <E0051>EAN008</E0051>
    </S001>
    <E0062>${referenceNumber}</E0062>
  </UNH>
  
  <UNB>
    <S001>
      <E0001>UNOC</E0001>
      <E0002>3</E0002>
    </S001>
    <S002>
      <E0004>${this.config.defaultSender.eicCode}</E0004>
      <E0007>14</E0007>
    </S002>
    <S003>
      <E0010>${this.getProviderEIC(request.provider)}</E0010>
      <E0007>14</E0007>
    </S003>
    <S004>
      <E0017>${now.toISOString().substring(0, 8)}</E0017>
      <E0019>${now.toISOString().substring(11, 15)}</E0019>
    </S004>
    <E0020>${referenceNumber}</E0020>
  </UNB>
  
  <BGM>
    <C002>
      <E1001>${request.messageType}</E1001>
      <E1131>ZZZ</E1131>
    </C002>
    <E1004>${referenceNumber}</E1004>
    <E1225>7</E1225>
  </BGM>
  
  <DTM>
    <C507>
      <E2005>137</E2005>
      <E2380>${now.toISOString().substring(0, 8)}</E2380>
      <E2379>102</E2379>
    </C507>
  </DTM>
  
  <RFF>
    <C506>
      <E1153>Z13</E1153>
      <E1154>${request.podId}</E1154>
    </C506>
  </RFF>
  
  <NAD>
    <E3035>MS</E3035>
    <C082>
      <E3039>${this.config.defaultSender.eicCode}</E3039>
      <E3055>9</E3055>
    </C082>
  </NAD>
  
  <NAD>
    <E3035>MR</E3035>
    <C082>
      <E3039>${this.getProviderEIC(request.provider)}</E3039>
      <E3055>9</E3055>
    </C082>
  </NAD>
  
  <UNS>
    <E0081>D</E0081>
  </UNS>
  
  <NAD>
    <E3035>IV</E3035>
    <C080>
      <E3036>${this.escapeXML(request.customerName)}</E3036>
    </C080>
    <C059>
      <E3042>${this.escapeXML(request.customerAddress)}</E3042>
    </C059>
  </NAD>
  
  <LOC>
    <E3227>172</E3227>
    <C517>
      <E3225>${request.podId}</E3225>
      <E3055>9</E3055>
    </C517>
  </LOC>
  
  <DTM>
    <C507>
      <E2005>194</E2005>
      <E2380>${this.formatContractDate(request.contractStartDate)}</E2380>
      <E2379>102</E2379>
    </C507>
  </DTM>
  
  <DTM>
    <C507>
      <E2005>206</E2005>
      <E2380>${this.formatContractDate(request.contractEndDate)}</E2380>
      <E2379>102</E2379>
    </C507>
  </DTM>
  
  <RFF>
    <C506>
      <E1153>Z02</E1153>
      <E1154>${this.mapMessageTypeToProcess(request.messageType)}</E1154>
    </C506>
  </RFF>
  
  <NAD>
    <E3035>Z01</E3035>
    <C082>
      <E3039>${request.newTrader}</E3039>
      <E3055>9</E3055>
    </C082>
  </NAD>
  
  ${request.oldTrader ? `<NAD>
    <E3035>Z02</E3035>
    <C082>
      <E3039>${request.oldTrader}</E3039>
      <E3055>9</E3055>
    </C082>
  </NAD>` : ''}
  
  <UNT>
    <E0074>1</E0074>
    <E0062>${referenceNumber}</E0062>
  </UNT>
</UTILMD>`;

    return this.config.output.prettifyXML ? this.prettifyXML(xml) : xml;
  }

  /**
   * Fájlnév generálása a választott formátum szerint
   */
  public generateFileName(request: PODChangeRequest): string {
    const now = new Date();
    const dateStr = now.toISOString().substring(0, 10).replace(/-/g, '');
    const timeStr = now.toISOString().substring(11, 19).replace(/:/g, '');
    
    if (this.config.format === 'ISU_UTILMD') {
      // Magyar ISU_UTILMD fájlnév konvenció
      return `TA_${this.getProviderSAPCode(request.provider)}-S${request.podId.slice(-20)}_${dateStr}_${timeStr}_${this.generateSequenceNumber()}.xml`;
    } else {
      // Standard EDIFACT fájlnév konvenció
      return `UTILMD_${request.provider}_${this.config.defaultSender.eicCode}_${dateStr}_${timeStr}_001.xml`;
    }
  }

  // === SEGÉD METÓDUSOK ===

  private extendRequest(request: PODChangeRequest | ExtendedPODChangeRequest): ExtendedPODChangeRequest {
    const extended = request as ExtendedPODChangeRequest;
    if (!extended.marketSettings) {
      extended.marketSettings = this.config.marketSettings;
    }
    return extended;
  }

  private mapMessageTypeToISU(messageType: string): string {
    const mapping = {
      'C01': 'E02', // Switch request -> Switch response
      'C03': 'E15', // Cancellation -> Cancellation response  
      'E01': 'E01'  // Notification -> Notification
    };
    return mapping[messageType as keyof typeof mapping] || 'E02';
  }

  private mapMessageTypeToProcess(messageType: string): string {
    const mapping = {
      'C01': 'E03', // Switch request
      'C03': 'E15', // Cancellation
      'E01': 'E02'  // Notification
    };
    return mapping[messageType as keyof typeof mapping] || 'E03';
  }

  private getProviderEIC(provider: string): string {
    const eicCodes = {
      'EON': 'HU000130F11-T',
      'ELMU': 'HU000210F11-T', 
      'DEMASZ': 'HU000310F11-T',
      'OPUS': 'HU000410F11-T',
      'MVMI': 'HU000510F11-T'
    };
    return eicCodes[provider as keyof typeof eicCodes] || 'HU000000F11-T';
  }

  private getProviderSAPCode(provider: string): string {
    const sapCodes = {
      'EON': 'HU000130F11',
      'ELMU': 'HU000210F11',
      'DEMASZ': 'HU000310F11', 
      'OPUS': 'HU000410F11',
      'MVMI': 'HU000510F11'
    };
    return sapCodes[provider as keyof typeof sapCodes] || 'HU000000F11';
  }

  private formatTimestamp(date: Date): string {
    return date.toISOString().replace(/[-:]/g, '').substring(0, 15);
  }

  private formatSAPDate(date: Date): string {
    return date.toISOString().substring(0, 10).replace(/-/g, '');
  }

  private formatSAPTime(date: Date): string {
    return date.toISOString().substring(11, 19).replace(/:/g, '');
  }

  private formatContractDate(dateStr: string): string {
    return dateStr.replace(/-/g, '');
  }

  private generateDocumentNumber(request: ExtendedPODChangeRequest, date: Date): string {
    const timestamp = this.formatTimestamp(date);
    return `${this.getProviderSAPCode(request.provider)}-${request.podId.slice(-12)}-${timestamp}`;
  }

  private generateSequenceNumber(): string {
    return Math.floor(Math.random() * 10000).toString().padStart(4, '0');
  }

  private escapeXML(text: string): string {
    if (!text) return '';
    return text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&apos;');
  }

  private prettifyXML(xml: string): string {
    // Egyszerű XML formázás
    const formatted = xml
      .replace(/></g, '>\n<')
      .split('\n')
      .map((line, index) => {
        const indent = '  '.repeat(Math.max(0, (line.match(/</g) || []).length - (line.match(/\//g) || []).length));
        return indent + line.trim();
      })
      .join('\n');
    
    return formatted;
  }

  // === VALIDÁCIÓS METÓDUSOK ===

  public validateRequest(request: PODChangeRequest): string[] {
    const errors: string[] = [];

    if (this.config.validation.strictPODFormat) {
      if (!request.podId.match(/^HU\d{6}-\d{2}-S\d{20}$/)) {
        errors.push('POD azonosító formátuma hibás. Helyes formátum: HU000000-00-S00000000000000000000');
      }
    }

    if (!request.customerName.trim()) {
      errors.push('Ügyfél neve kötelező');
    }

    if (!request.customerAddress.trim()) {
      errors.push('Ügyfél címe kötelező');
    }

    if (this.config.validation.checkDateRanges) {
      if (!request.contractStartDate) {
        errors.push('Szerződés kezdete kötelező');
      }

      if (!request.contractEndDate) {
        errors.push('Szerződés vége kötelező');
      }

      if (request.contractStartDate && request.contractEndDate) {
        const startDate = new Date(request.contractStartDate);
        const endDate = new Date(request.contractEndDate);
        if (startDate >= endDate) {
          errors.push('Szerződés vége nem lehet korábbi a kezdeténél');
        }
      }
    }

    if (this.config.validation.validateEICCodes) {
      if (!request.newTrader.match(/^[A-Z0-9]{2}[A-Z0-9\-]{13}[A-Z0-9]$/)) {
        errors.push('Új kereskedő EIC kód formátuma hibás');
      }

      if (request.oldTrader && !request.oldTrader.match(/^[A-Z0-9]{2}[A-Z0-9\-]{13}[A-Z0-9]$/)) {
        errors.push('Régi kereskedő EIC kód formátuma hibás');
      }
    }

    return errors;
  }

  // === STATIKUS METÓDUSOK ===

  /**
   * Gyors ISU_UTILMD generálás magyar piacra optimalizált beállításokkal
   */
  static generateHungarianISU(request: PODChangeRequest): string {
    const generator = new UTILMDGenerator({
      format: 'ISU_UTILMD',
      marketSettings: {
        format: 'ISU_UTILMD',
        market: 'HU',
        version: '3.0',
        encoding: 'UTF-8'
      }
    });
    return generator.generateXML(request);
  }

  /**
   * Gyors EDIFACT generálás EU szabvány szerint
   */
  static generateEuropeanEDIFACT(request: PODChangeRequest): string {
    const generator = new UTILMDGenerator({
      format: 'EDIFACT',
      marketSettings: {
        format: 'EDIFACT', 
        market: 'EU',
        version: '3.0',
        encoding: 'UTF-8'
      }
    });
    return generator.generateXML(request);
  }
} 